import React, { useState } from 'react';
import { useMutation, useApolloClient } from '@apollo/client';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { fareShape, historyShape, currencyShape, productShape, productsShape, legShape } from 'constants/propTypes';
import { ConfirmButton, CancelButton } from 'components/ui/Button';
import { Row, Column } from 'components/shared/Layout';
import { StyledColumn } from 'pages/BookingDetails/Journeys/AfterSalesWarning/AfterSalesWarning.styles';
import { bookingStatus } from 'types/booking';
import { UPDATE_EXTRAS_SWAP, UPDATE_EXTRAS, CANCEL_CONFIRMED_BOOKING, ADD_ADDITIONAL_DETAILS, OVERRIDE_PRICE } from 'store/mutations';
import { GET_EXTRA_PRODUCTS } from 'store/queries';

import moment from 'moment';
import AgentPortalError from 'errors/AgentPortalError';
import { productFamilyCodes } from 'types/productFamilyCodes';
import Modal from 'components/ui/Modal';
import { MOBILE_TERMINAL_ID_STORAGE_KEY } from 'constants/user';
import serviceTypeCodes from 'constants/serviceTypeCodes';
import { DIRECTIONS } from 'types/classes';
import { isTryingToDowngrade } from '../../Exchange.utils';
import {
  buildESRequest,
  buildExtraRequest,
  buildFeeRequest,
  buildSwapRequest,
  getExtrasVariables,
  getPassengersBeingExchanged,
  getExtrasBeingExchanged,
  rebookExtrasRequest,
  filterRebookExtras
} from './ExchangeConfirmSelection.utils';
import ExchangeConfirmSelectionWithWarning from '../ExchangeConfirmSelectionWithWarning';
import { calcSegmentPointsTotal } from '../../../JourneySearch/JourneySearch.utils';
import getReplaceItems from 'utils/getReplacedItems';
import useSummatedFaresOverrideMutation from 'components/mutations/useSummatedFaresOverrideMutation';
import useRebook from 'components/mutations/useRebook';

const ExchangeConfirmSelection = ({
  status,
  data,
  disabled,
  history,
  overrideReason,
  isFlowForward,
  isStationAgent,
  stationName,
  isExchangingOFETrain,
  outboundOriginalClassOfService = '',
  inboundOriginalClassOfService = '',
  swapStations,
  wasPurchasedWithSubscription = false,
  isMultiJourney = false,
  isSummatedFaresSelectionEnabled = false,
  isExchangingMultipleLegsOfAOutboundSummated = false,
  isExchangingSinglegOfAOutboundSummated = false,
  isExchangingSingleLegOfAInboundSummated = false,
  isExchangingMultipleLegsOfAInboundSummated = false,
  isExchangingOutboundDirectFare = false,
  isExchangingInboundDirectFare = false,
  isExchangingOutboundThroughFare = false,
  isExchangingInboundThroughFare = false,
  isThalysUser = false,
  hasSpecialLoyaltyTier = false,
  outboundOriginalClassesOfAllServices = [],
  inboundOriginalClassesOfAllServices = [],
  outboundLegsBeingExchanged = [],
  inboundLegsBeingExchanged = [],
  isPackageBooking = false
}) => {
  const client = useApolloClient();
  const [extrasLoading, setExtrasLoading] = useState(false);
  const [showDowngradeWarning, setShowDowngradeWarning] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [showNotEnoughPoints, setShowNotEnoughPoints] = useState(false);
  const membershipNumber = new URLSearchParams(history?.location?.search).get('membershipNumber');
  const isLoyaltyBooking = data?.productFamiliesWithFares?.some((pf) => pf.familyGroup === productFamilyCodes.EUROSTAR_LOYALTY);

  const passengersBeingExchanged = getPassengersBeingExchanged(data?.outboundSelectedFare, data?.inboundSelectedFare);
  const hasOutboundExtras = !!data?.outboundSelectedFare && data?.outboundExtras?.length > 0;
  const extrasOutbound = hasOutboundExtras ? getExtrasBeingExchanged(data?.outboundExtras, passengersBeingExchanged) : [];
  const hasInboundExtras = !!data?.inboundSelectedFare && data?.inboundExtras?.length > 0;
  const extrasInbound = hasInboundExtras ? getExtrasBeingExchanged(data?.inboundExtras, passengersBeingExchanged, DIRECTIONS.INBOUND) : [];
  const hasExtrasBeingExchanged = extrasOutbound.length > 0 || extrasInbound.length > 0;

  const esRequest = buildESRequest({
    data,
    isExchangingOFETrain,
    isFlowForward,
    overrideReason,
    isLoyaltyBooking,
    wasPurchasedWithSubscription
  });

  const feeRequest = buildFeeRequest({
    data,
    isExchangingOFETrain,
    isStationAgent,
    overrideReason,
    isFlowForward,
    wasPurchasedWithSubscription,
    isThalysUser,
    hasSpecialLoyaltyTier,
    isPackageBooking
  });

  const hasFeeRequestSegments = feeRequest?.segments?.length !== 0;

  const isNotDowngrading = () => {
    const { outboundSelectedFare, inboundSelectedFare } = data;

    if (
      isTryingToDowngrade(outboundOriginalClassesOfAllServices, outboundSelectedFare) ||
      isTryingToDowngrade(inboundOriginalClassesOfAllServices, inboundSelectedFare)
    ) {
      setShowDowngradeWarning(true);

      return false;
    }

    return true;
  };

  const redirectOnCompleted = () =>
    history.push(
      `/exchange/${data.reference}/pax?currency=${data.currency}${membershipNumber ? `&membershipNumber=${membershipNumber}` : ''}`
    );

  const [rebook, { loading: rebookLoading }] = useRebook();

  const [updateBooking, { loading: updateBookingLoading }] = useMutation(UPDATE_EXTRAS, {
    variables: feeRequest,
    onCompleted: () => redirectOnCompleted()
  });

  const [overridePricesSummatedFares, { loading: overridePricesSummatedFaresLoading }] = useSummatedFaresOverrideMutation();
  const [addAdditionalDetails, { loading: addAdditionalDetailsLoading }] = useMutation(ADD_ADDITIONAL_DETAILS);
  const [updateBookingSwap, { loading: updateBookingLoadingSwap }] = useMutation(UPDATE_EXTRAS_SWAP);
  const [cancelConfirmedBooking, { loading: cancelBookingLoading }] = useMutation(CANCEL_CONFIRMED_BOOKING, {
    variables: {
      bookingReference: data.reference,
      overrideReason: 'Swap stations in Voyager',
      overrideAftersalesRules: true
    }
  });

  const [overridePrice, { loading: overridePriceLoading }] = useMutation(OVERRIDE_PRICE);

  const { ON_HOLD, TEMPORARILY_ON_HOLD, PROVISIONAL } = bookingStatus;
  const isOnHoldOrProvisional = [ON_HOLD, TEMPORARILY_ON_HOLD, PROVISIONAL].includes(status);

  const handleExtrasFetch = async (currency, fare, extras) => {
    const extrasVariables = {
      currency,
      ...getExtrasVariables(fare, extras)
    };

    if (extrasVariables.passengers.length > 0 && extrasVariables.types.length > 0) {
      const results = await client.query({
        query: GET_EXTRA_PRODUCTS,
        variables: extrasVariables
      });

      const rebookExtras = results.data?.getExtraProducts?.extras;
      const filteredExtras = filterRebookExtras(rebookExtras, extras);
      return { extras: filteredExtras };
    }

    return [];
  };

  const handleOpen = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);
  const toggleNotEnoughPointsModal = () => setShowNotEnoughPoints(!showNotEnoughPoints);
  const toggleShowDowngrading = () => setShowDowngradeWarning(!showDowngradeWarning);

  const hasEnoughPoints = () => {
    const { outboundSelectedFare, inboundSelectedFare, isMember, activePoints } = data;
    const outboundTotalPoints = calcSegmentPointsTotal(outboundSelectedFare);
    const inboundTotalPoints = calcSegmentPointsTotal(inboundSelectedFare);

    if (isMember && isLoyaltyBooking) {
      if ((outboundTotalPoints || 0) + (inboundTotalPoints || 0) > activePoints) {
        setShowNotEnoughPoints(true);

        return false;
      }
    }

    return true;
  };

  const fetchExtras = async () => {
    setExtrasLoading(true);

    const outboundExtrasAvailability =
      extrasOutbound.length > 0 ? await handleExtrasFetch(data?.currency, data?.outboundSelectedFare, data?.outboundExtras) : [];

    const inboundExtrasAvailability =
      extrasInbound.length > 0 ? await handleExtrasFetch(data?.currency, data?.inboundSelectedFare, data?.inboundExtras) : [];

    const outboundRebookExtras = rebookExtrasRequest(outboundExtrasAvailability, extrasOutbound);
    const inboundRebookExtras = rebookExtrasRequest(inboundExtrasAvailability, extrasInbound);

    setExtrasLoading(false);

    return {
      outboundRebookExtras,
      inboundRebookExtras
    };
  };

  const confirmExchange = async () => {
    if (swapStations) {
      try {
        setBookingLoading(true);

        await cancelConfirmedBooking();

        let updatedBooking = null;
        if (hasExtrasBeingExchanged) {
          const { outboundRebookExtras, inboundRebookExtras } = await fetchExtras();

          const swapWithExtrasRequest = buildSwapRequest({
            data,
            outboundExtras: outboundRebookExtras,
            inboundExtras: inboundRebookExtras
          });

          updatedBooking = await updateBookingSwap({
            variables: swapWithExtrasRequest
          });
        } else {
          const swapRequest = buildSwapRequest({
            data
          });

          updatedBooking = await updateBookingSwap({
            variables: swapRequest
          });
        }

        const booking = updatedBooking?.data?.updateBookingExtras ?? {};

        if (booking && booking?.pendingCancellationLegs?.length > 0 && booking?.pendingCreationLegs?.length > 0) {
          const updatePrice = [];
          const updateAdditionalDetails = [];

          booking.pendingCancellationLegs
            .filter(({ serviceType: { code } }) => [serviceTypeCodes.EUROSTAR, serviceTypeCodes.GREENSPEED].includes(code))
            .forEach(({ bookingItems }, legIndex) => {
              bookingItems.forEach(({ passengerId, price }, itemIndex) => {
                const createItem = booking.pendingCreationLegs?.[legIndex]?.bookingItems?.[itemIndex];

                if (createItem?.passengerId === passengerId && createItem?.price < price) {
                  updatePrice.push({
                    price,
                    itemRefId: Number(createItem.itemRef)
                  });
                  updateAdditionalDetails.push({
                    itemRefId: Number(createItem.itemRef),
                    key: 'original_price',
                    value: String(createItem.price)
                  });
                }
              });
            });

          if (updatePrice.length > 0 && updateAdditionalDetails.length > 0) {
            await overridePrice({
              variables: {
                reference: data.reference,
                items: updatePrice
              }
            });

            await addAdditionalDetails({
              variables: {
                bookingReference: data.reference,
                additionalDetails: updateAdditionalDetails
              }
            });
          }
        }

        setBookingLoading(false);
        redirectOnCompleted();
      } catch (e) {
        setBookingLoading(false);
        setError(e);
      }
    } else {
      try {
        let replaceItems = [];

        if (isMultiJourney) {
          replaceItems = getReplaceItems(
            data?.outboundSelectedFare,
            data?.inboundSelectedFare,
            outboundLegsBeingExchanged,
            inboundLegsBeingExchanged
          );
        }

        let rebookResult;

        if (hasExtrasBeingExchanged) {
          const { outboundRebookExtras, inboundRebookExtras } = await fetchExtras();
          rebookResult = await rebook({
            variables: {
              ...buildExtraRequest({
                data,
                isExchangingOFETrain,
                isFlowForward,
                overrideReason,
                outboundExtras: outboundRebookExtras,
                inboundExtras: inboundRebookExtras,
                isLoyaltyBooking
              }),
              replaceItems
            }
          });
        } else {
          rebookResult = await rebook({
            variables: {
              ...esRequest,
              replaceItems
            }
          });
        }

        if (isSummatedFaresSelectionEnabled) {
          await overridePricesSummatedFares({
            variables: {
              rebookData: rebookResult?.rebook,
              originalData: data,
              reference: data.reference,
              isExchangingMultipleLegsOfAOutboundSummated,
              isExchangingSinglegOfAOutboundSummated,
              isExchangingMultipleLegsOfAInboundSummated,
              isExchangingSingleLegOfAInboundSummated,
              isExchangingOutboundDirectFare,
              isExchangingInboundDirectFare,
              isExchangingOutboundThroughFare,
              isExchangingInboundThroughFare,
              isFreeExchange: data?.isAOPActivated && data?.isMaintainPriceActivated,
              exchangingOutboundSameClassOfService: outboundOriginalClassOfService === data?.outboundSelectedFare?.classOfService?.code,
              exchangingInboundSameClassOfService: inboundOriginalClassOfService === data?.inboundSelectedFare?.classOfService?.code
            }
          });
        }

        if (hasFeeRequestSegments) {
          await updateBooking();
        }

        if (isStationAgent && localStorage.getItem(MOBILE_TERMINAL_ID_STORAGE_KEY)) {
          await addAdditionalDetails({
            variables: {
              bookingReference: data.reference,
              additionalDetails: [
                {
                  key: `tablet-exchange-${moment().format('HH:mm:ss.DDMMYY')}`,
                  value: stationName
                }
              ]
            }
          });
        }

        redirectOnCompleted();
      } catch (e) {
        setError(e);
      }
    }
  };

  const handleConfirmExchange = async (_, acceptedDowngrade = false) => {
    if (isFlowForward) {
      const fare = data?.outboundSelectedFare || data?.inboundSelectedFare;

      if (fare && fare.canBeBooked && fare.cappedTrain && (acceptedDowngrade || isNotDowngrading())) {
        const itemRefs = (data.existingTrains.outbound || data.existingTrains.inbound).products.map((p) => p.itemRef);
        const cappedTrain = fare.cappedTrain;

        history.push(
          `/exchange/${data.reference}/flow-forward-capped/pax?bookingItems=${itemRefs.join(
            ','
          )}&recommendedSeats=${cappedTrain.recommendedSeats.map((seat) => `${seat.coach}:${seat.number}`).join(',')}&nextAvailableTrain=${
            cappedTrain.trainNumber
          }&serviceIdentifier=${cappedTrain.serviceIdentifier}${membershipNumber ? `&membershipNumber=${membershipNumber}` : ''}`
        );

        return;
      }
    }

    if (hasEnoughPoints() && (acceptedDowngrade || isNotDowngrading())) {
      await confirmExchange();
    }
  };

  if (error) {
    throw new AgentPortalError(error);
  }

  return (
    <>
      <Modal isOpen={showNotEnoughPoints} onClose={toggleNotEnoughPointsModal} data-id="modal-not-enough-points">
        <FormattedMessage id="journeySummary.notEnoughPoints" />
      </Modal>

      <Modal isOpen={showDowngradeWarning} onClose={toggleShowDowngrading} data-id="modal-show-downgrading-warning">
        <Row>
          <Column>
            <FormattedMessage id="exchange.tryingToDowngradeMessage" data-id="exchange-tryingToDowngradeMessage" />
            {isLoyaltyBooking && (
              <div>
                <FormattedMessage id="exchange.tryingToDowngradeWithPointsMessage" data-id="exchange-tryingToDowngradeWithPointsMessage" />
              </div>
            )}
          </Column>
          <StyledColumn span={3}>
            <ConfirmButton
              data-cy="exchanges-continue-with-downgrade"
              onClick={async (e) => {
                await handleConfirmExchange(e, true);
              }}
              disabled={
                disabled ||
                extrasLoading ||
                rebookLoading ||
                updateBookingLoading ||
                addAdditionalDetailsLoading ||
                overridePricesSummatedFaresLoading
              }
              loading={rebookLoading || updateBookingLoading || addAdditionalDetailsLoading || overridePricesSummatedFaresLoading}
              variant="secondary"
            >
              <FormattedMessage id="exchanges.continue" />
            </ConfirmButton>
          </StyledColumn>

          <StyledColumn span={3}>
            <CancelButton
              data-testid="exchanges-cancel-with-downgrade"
              data-cy="exchanges-cancel-with-downgrade"
              onClick={() => {
                setShowDowngradeWarning(!showDowngradeWarning);
              }}
            >
              <FormattedMessage id="extras.cancel" />
            </CancelButton>
          </StyledColumn>
        </Row>
      </Modal>

      {isOnHoldOrProvisional ? (
        <ExchangeConfirmSelectionWithWarning
          isOpen={isOpen}
          onOpen={handleOpen}
          onClose={handleClose}
          data={data}
          disabled={disabled}
          overrideReason={overrideReason}
          isFlowForward={isFlowForward}
          isStationAgent={isStationAgent}
          stationName={stationName}
          isExchangingOFETrain={isExchangingOFETrain}
          outboundOriginalClassOfService={outboundOriginalClassOfService}
          inboundOriginalClassOfService={inboundOriginalClassOfService}
          isLoyaltyBooking={isLoyaltyBooking}
          hasSpecialLoyaltyTier={hasSpecialLoyaltyTier}
          isMultiJourney={isMultiJourney}
          isSummatedFaresSelectionEnabled={isSummatedFaresSelectionEnabled}
          isExchangingMultipleLegsOfAOutboundSummated={isExchangingMultipleLegsOfAOutboundSummated}
          isExchangingSinglegOfAOutboundSummated={isExchangingSinglegOfAOutboundSummated}
          isExchangingMultipleLegsOfAInboundSummated={isExchangingMultipleLegsOfAInboundSummated}
          isExchangingSingleLegOfAInboundSummated={isExchangingSingleLegOfAInboundSummated}
          isExchangingOutboundDirectFare={isExchangingOutboundDirectFare}
          isExchangingInboundDirectFare={isExchangingInboundDirectFare}
          isExchangingOutboundThroughFare={isExchangingOutboundThroughFare}
          isExchangingInboundThroughFare={isExchangingInboundThroughFare}
          outboundOriginalClassesOfAllServices={outboundOriginalClassesOfAllServices}
          inboundOriginalClassesOfAllServices={inboundOriginalClassesOfAllServices}
          outboundLegsBeingExchanged={outboundLegsBeingExchanged}
          inboundLegsBeingExchanged={inboundLegsBeingExchanged}
          isProvisional={[PROVISIONAL].includes(status)}
        />
      ) : (
        <ConfirmButton
          data-cy="exchanges-continue"
          onClick={handleConfirmExchange}
          disabled={
            disabled ||
            extrasLoading ||
            rebookLoading ||
            updateBookingLoading ||
            updateBookingLoadingSwap ||
            addAdditionalDetailsLoading ||
            cancelBookingLoading ||
            overridePriceLoading ||
            bookingLoading ||
            overridePricesSummatedFaresLoading
          }
          loading={
            rebookLoading ||
            extrasLoading ||
            updateBookingLoading ||
            addAdditionalDetailsLoading ||
            cancelBookingLoading ||
            overridePriceLoading ||
            updateBookingLoadingSwap ||
            bookingLoading ||
            overridePricesSummatedFaresLoading
          }
          variant="primary"
        >
          <FormattedMessage id="exchanges.continue" />
        </ConfirmButton>
      )}
    </>
  );
};

ExchangeConfirmSelection.propTypes = {
  status: PropTypes.string,
  data: PropTypes.shape({
    outboundSelectedFare: fareShape,
    inboundSelectedFare: fareShape,
    outboundExtras: PropTypes.arrayOf(productShape),
    inboundExtras: PropTypes.arrayOf(productShape),
    reference: PropTypes.string.isRequired,
    currency: currencyShape,
    productFamiliesWithFares: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        code: PropTypes.string
      })
    ),
    isMember: PropTypes.bool,
    activePoints: PropTypes.number,
    existingTrains: PropTypes.shape({
      outbound: PropTypes.shape({
        products: productShape
      }),
      inbound: PropTypes.shape({
        products: productShape
      }),
      products: productsShape
    }),
    isAOPActivated: PropTypes.bool,
    isMaintainPriceActivated: PropTypes.bool
  }).isRequired,
  history: historyShape.isRequired,
  disabled: PropTypes.bool,
  overrideReason: PropTypes.string,
  isFlowForward: PropTypes.bool,
  isStationAgent: PropTypes.bool,
  isExchangingOFETrain: PropTypes.bool,
  outboundOriginalClassOfService: PropTypes.string,
  inboundOriginalClassOfService: PropTypes.string,
  swapStations: PropTypes.bool,
  stationName: PropTypes.string,
  isMultiJourney: PropTypes.bool,
  isSummatedFaresSelectionEnabled: PropTypes.bool,
  isExchangingMultipleLegsOfAOutboundSummated: PropTypes.bool,
  isExchangingMultipleLegsOfAInboundSummated: PropTypes.bool,
  isExchangingOutboundDirectFare: PropTypes.bool,
  isExchangingInboundDirectFare: PropTypes.bool,
  isExchangingOutboundThroughFare: PropTypes.bool,
  isExchangingInboundThroughFare: PropTypes.bool,
  wasPurchasedWithSubscription: PropTypes.bool,
  isThalysUser: PropTypes.bool,
  hasSpecialLoyaltyTier: PropTypes.bool,
  outboundOriginalClassesOfAllServices: PropTypes.arrayOf(PropTypes.string),
  inboundOriginalClassesOfAllServices: PropTypes.arrayOf(PropTypes.string),
  isExchangingSinglegOfAOutboundSummated: PropTypes.bool,
  isExchangingSingleLegOfAInboundSummated: PropTypes.bool,
  outboundLegsBeingExchanged: PropTypes.arrayOf(legShape),
  inboundLegsBeingExchanged: PropTypes.arrayOf(legShape),
  isPackageBooking: PropTypes.bool
};

ExchangeConfirmSelection.defaultProps = {
  disabled: false,
  isFlowForward: false,
  isStationAgent: false,
  isExchangingOFETrain: false
};

export default ExchangeConfirmSelection;
